/**
 * Payment Success Page
 * 
 * Professional success page for payments and debit order authorization
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { paymentAPI } from '../../services/api';
import './PaymentSuccess.css';

// SVG Icons as components for better rendering
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <polyline points="9 12 11 14 15 10"></polyline>
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const BankIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18"></path>
    <path d="M3 10h18"></path>
    <path d="M5 6l7-3 7 3"></path>
    <path d="M4 10v11"></path>
    <path d="M20 10v11"></path>
    <path d="M8 14v3"></path>
    <path d="M12 14v3"></path>
    <path d="M16 14v3"></path>
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState<any>(null);
  const paymentId = searchParams.get('payment_id');
  
  const mPaymentId = searchParams.get('m_payment_id');
  const paymentStatus = searchParams.get('payment_status');
  const pfPaymentId = searchParams.get('pf_payment_id');
  
  const isDebitOrder = location.pathname.includes('debit-order');

  useEffect(() => {
    // Debug: Log all URL parameters
    console.log('🔗 PaymentSuccess URL params:', {
      fullUrl: window.location.href,
      mPaymentId,
      paymentStatus,
      pfPaymentId,
      paymentId,
      allParams: searchParams.toString()
    });

    if (isDebitOrder) {
      setLoading(false);
      return;
    }

    // PayFast returns with m_payment_id - verify even without payment_status
    if (mPaymentId) {
      const verifyPayment = async () => {
        try {
          // Add payment_status=complete if not provided (PayFast only redirects to success URL on success)
          const verifyParams = new URLSearchParams(searchParams.toString());
          if (!verifyParams.has('payment_status')) {
            verifyParams.set('payment_status', 'complete');
          }
          
          const apiUrl = process.env.REACT_APP_API_URL || '';
          console.log('🔍 Verifying PayFast payment:', mPaymentId);
          console.log('🌐 API URL:', apiUrl);
          console.log('📝 Verify params:', verifyParams.toString());
          
          const response = await fetch(`${apiUrl}/payments/payfast/verify?${verifyParams.toString()}`);
          const data = await response.json();
          
          console.log('📥 Verification response:', data);
          
          if (data.success && data.data) {
            setPaymentData({
              paymentReference: data.data.paymentReference,
              status: data.data.status,
              payfastPaymentId: data.data.payfastPaymentId,
              amount: data.data.amount || 0
            });
            
            if (data.data.paymentId) {
              try {
                const statusResponse = await paymentAPI.getPaymentStatus(data.data.paymentId);
                if (statusResponse.success && statusResponse.data) {
                  setPaymentData(statusResponse.data);
                }
              } catch (error) {
                console.error('Error fetching payment details:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
        } finally {
          setLoading(false);
        }
      };

      verifyPayment();
    } else if (paymentId) {
      const fetchPaymentStatus = async () => {
        try {
          const response = await paymentAPI.getPaymentStatus(paymentId);
          if (response.success && response.data) {
            setPaymentData(response.data);
          }
        } catch (error) {
          console.error('Error fetching payment status:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [paymentId, mPaymentId, paymentStatus, pfPaymentId, searchParams, isDebitOrder]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="success-page">
        <div className="success-card">
          <div className="loading-state">
            <div className="loader"></div>
            <p>Verifying your transaction...</p>
          </div>
        </div>
      </div>
    );
  }

  // Debit Order Success
  if (isDebitOrder) {
    return (
      <div className="success-page">
        <div className="success-card">
          <div className="success-header">
            <div className="icon-wrapper icon-success">
              <CheckIcon />
            </div>
            <h1>DebiCheck mandate approved</h1>
            <p className="subtitle">Your bank has confirmed the mandate. Repayments will be collected automatically on your chosen day.</p>
          </div>

          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon">
                <ShieldIcon />
              </div>
              <div className="info-content">
                <h3>Secure & Protected</h3>
                <p>Your banking details are encrypted and stored securely</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <CalendarIcon />
              </div>
              <div className="info-content">
                <h3>Automatic Payments</h3>
                <p>Payments will be collected on your selected debit day each month</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <BankIcon />
              </div>
              <div className="info-content">
                <h3>Easy Management</h3>
                <p>View and manage your payments from your dashboard anytime</p>
              </div>
            </div>
          </div>

          <div className="action-section">
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              <span>Go to Dashboard</span>
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment Success
  return (
    <div className="success-page">
      <div className="success-card">
        <div className="success-header">
          <div className="icon-wrapper icon-success">
            <CheckIcon />
          </div>
          <h1>Payment Successful</h1>
          <p className="subtitle">Your transaction has been completed</p>
        </div>

        {paymentData && (
          <div className="transaction-details">
            <h2>Transaction Details</h2>
            <div className="details-grid">
              <div className="detail-row">
                <span className="label">Reference</span>
                <span className="value">{paymentData.paymentReference}</span>
              </div>
              <div className="detail-row">
                <span className="label">Amount</span>
                <span className="value amount">{formatCurrency(paymentData.amount)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Status</span>
                <span className="value">
                  <span className="status-badge status-success">{paymentData.status}</span>
                </span>
              </div>
              {paymentData.payfastPaymentId && (
                <div className="detail-row">
                  <span className="label">Transaction ID</span>
                  <span className="value mono">{paymentData.payfastPaymentId}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="confirmation-message">
          <p>A confirmation has been sent to your registered email address.</p>
        </div>

        <div className="action-section">
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            <span>Return to Dashboard</span>
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
